import useBacktestStore from '../Store/backtestStore';
import {
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Area,
  ComposedChart
} from 'recharts';

/* ---------- helpers ---------- */

function num(v, opts = {}) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, opts);
}

function money(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}

// daily_return is ALREADY a percent
function pctAlready(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(2)}%`;
}

function pct(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(2)}%`;
}

function dateOnly(s) {
  if (!s) return '—';
  return String(s).slice(0, 10);
}

function shortDate(s) {
  if (!s) return '—';

  const d = new Date(`${dateOnly(s)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateOnly(s);

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

function lastNDaysRows(rows, days = 10) {
  if (!rows?.length) return [];

  const lastDate = new Date(rows[rows.length - 1].date);
  const cutoff = new Date(lastDate);
  cutoff.setDate(cutoff.getDate() - (days - 1));

  return rows.filter((r) => {
    const d = new Date(r.date);
    return d >= cutoff;
  });
}

function getValidEquity(rows) {
  return rows
    .map((r) => Number(r.equity))
    .filter(Number.isFinite);
}

function getMaxDrawdown(rows) {
  let peak = -Infinity;
  let maxDrawdown = 0;
  let maxDrawdownPct = 0;

  for (const row of rows) {
    const equity = Number(row.equity);
    if (!Number.isFinite(equity)) continue;

    peak = Math.max(peak, equity);

    const drawdown = equity - peak;
    const drawdownPct = peak > 0 ? drawdown / peak : 0;

    if (drawdown < maxDrawdown) {
      maxDrawdown = drawdown;
      maxDrawdownPct = drawdownPct;
    }
  }

  return {
    maxDrawdown,
    maxDrawdownPct
  };
}

/* ---------- tooltip ---------- */

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row || row.period === 'PAD') return null;

  const equity = row.equity;
  const dailyPnl = row.daily_pnl;
  const dailyReturn = row.daily_return;

  return (
    <div
      style={{
        minWidth: 220,
        background:
          'linear-gradient(180deg, rgba(18,22,32,0.98), rgba(8,10,15,0.98))',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 14,
        boxShadow: '0 18px 45px rgba(0,0,0,0.55)',
        color: 'rgba(255,255,255,0.94)',
        padding: '12px 14px'
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.52)',
          marginBottom: 7
        }}
      >
        {shortDate(label)} • {row.period}
      </div>

      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 10
        }}
      >
        {money(equity)}
      </div>

      <div
        style={{
          display: 'grid',
          gap: 6,
          fontSize: 12
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Daily PnL</span>
          <span
            style={{
              fontWeight: 700,
              color:
                Number(dailyPnl) > 0
                  ? '#37d67a'
                  : Number(dailyPnl) < 0
                    ? '#ff5c7a'
                    : 'rgba(255,255,255,0.85)'
            }}
          >
            {money(dailyPnl)}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Daily Return</span>
          <span
            style={{
              fontWeight: 700,
              color:
                Number(dailyReturn) > 0
                  ? '#37d67a'
                  : Number(dailyReturn) < 0
                    ? '#ff5c7a'
                    : 'rgba(255,255,255,0.85)'
            }}
          >
            {pctAlready(dailyReturn)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- main chart ---------- */

export default function EquityChart() {
  const equitySeries = useBacktestStore((s) => s.equitySeries);

  if (!equitySeries?.length) {
    return <div className="mutedLine">No equity data yet.</div>;
  }

  const LIVE_START = '2025-11-03';
  const START_EQUITY = 100000;

  const first = equitySeries[0];
  const last = equitySeries[equitySeries.length - 1];

  const allEquityVals = getValidEquity(equitySeries);
  const dataMin = allEquityVals.length ? Math.min(...allEquityVals) : START_EQUITY;
  const dataMax = allEquityVals.length ? Math.max(...allEquityVals) : START_EQUITY;

  const yPadding = Math.max((dataMax - dataMin) * 0.16, 2500);
  const yMinPadded = Math.max(0, Math.floor((dataMin - yPadding) / 5000) * 5000);
  const yMaxPadded = Math.ceil((dataMax + yPadding) / 5000) * 5000;

  const endingEquity = Number(last.equity);
  const netProfit = endingEquity - START_EQUITY;
  const totalReturnPct = START_EQUITY > 0 ? (netProfit / START_EQUITY) * 100 : null;

  const wins = equitySeries.filter((r) => Number(r.daily_pnl) > 0).length;
  const losses = equitySeries.filter((r) => Number(r.daily_pnl) < 0).length;
  const closedDays = wins + losses;
  const winRate = closedDays > 0 ? (wins / closedDays) * 100 : null;

  const { maxDrawdown, maxDrawdownPct } = getMaxDrawdown(equitySeries);

  const baseChartData = equitySeries.map((r) => {
    const d = dateOnly(r.date);
    const isLive = d >= LIVE_START;
    const equity = Number(r.equity);

    return {
      date: d,
      equity,
      equity_fill: equity,

      daily_pnl:
        r.daily_pnl !== null && r.daily_pnl !== undefined
          ? Number(r.daily_pnl)
          : null,

      daily_return:
        r.daily_return !== null && r.daily_return !== undefined
          ? Number(r.daily_return)
          : null,

      equity_backtest: isLive ? null : equity,
      equity_live: isLive ? equity : null,

      period: isLive ? 'LIVE' : 'BACKTEST'
    };
  });

  /*
    Bridge the visual gap between the last backtest day
    and the first live day.
  */
  const liveStartIndex = baseChartData.findIndex((r) => r.period === 'LIVE');

  if (liveStartIndex > 0) {
    const priorBacktestPoint = baseChartData[liveStartIndex - 1];

    baseChartData[liveStartIndex - 1] = {
      ...priorBacktestPoint,
      equity_live: priorBacktestPoint.equity,
      period: 'BACKTEST_TO_LIVE'
    };
  }

  /* ---------- ghost point for right padding ---------- */
  const lastDateObj = new Date(last.date);
  const ghostDateObj = new Date(lastDateObj);
  ghostDateObj.setDate(ghostDateObj.getDate() + 10);
  const ghostDate = ghostDateObj.toISOString().slice(0, 10);

  const chartData = [
    ...baseChartData,
    {
      date: ghostDate,
      equity: null,
      equity_fill: null,
      daily_pnl: null,
      daily_return: null,
      equity_backtest: null,
      equity_live: null,
      period: 'PAD'
    }
  ];

  const tableRows = lastNDaysRows(equitySeries, 11);

  return (
    <div>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 24,
          border: '1px solid rgba(255,255,255,0.10)',
          background:
            'radial-gradient(circle at 15% 0%, rgba(177,136,0,0.18), transparent 34%), radial-gradient(circle at 85% 12%, rgba(255,255,255,0.08), transparent 28%), linear-gradient(180deg, rgba(15,18,27,0.98), rgba(5,7,11,0.98))',
          boxShadow:
            '0 24px 70px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
          padding: 22
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 'auto -80px -130px auto',
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'rgba(177,136,0,0.12)',
            filter: 'blur(45px)',
            pointerEvents: 'none'
          }}
        />

        {/* ---------- HEADER ---------- */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 18,
            alignItems: 'flex-start',
            marginBottom: 18
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.52)',
                fontWeight: 800,
                marginBottom: 7
              }}
            >
              MEIC Performance Curve
            </div>

            <div
              style={{
                fontSize: 28,
                lineHeight: 1.05,
                fontWeight: 900,
                color: 'rgba(255,255,255,0.96)'
              }}
            >
              {money(endingEquity)}
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                color: 'rgba(255,255,255,0.58)'
              }}
            >
              {shortDate(first.date)} → {shortDate(last.date)} • {num(equitySeries.length)} sessions
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}
          >
            <div className="miniStatModern">
              <div className="miniLabelModern">Start</div>
              <div className="miniValueModern">{money(START_EQUITY)}</div>
            </div>

            <div className="miniStatModern">
              <div className="miniLabelModern">Net Profit</div>
              <div
                className="miniValueModern"
                style={{
                  color: netProfit >= 0 ? '#37d67a' : '#ff5c7a'
                }}
              >
                {money(netProfit)}
              </div>
            </div>

            <div className="miniStatModern">
              <div className="miniLabelModern">Return</div>
              <div
                className="miniValueModern"
                style={{
                  color: Number(totalReturnPct) >= 0 ? '#37d67a' : '#ff5c7a'
                }}
              >
                {pct(totalReturnPct)}
              </div>
            </div>

            <div className="miniStatModern">
              <div className="miniLabelModern">Win Rate</div>
              <div className="miniValueModern">{pct(winRate)}</div>
            </div>
          </div>
        </div>

        {/* ---------- SUB METRICS ---------- */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 10,
            marginBottom: 16
          }}
        >
          <div className="metricTile">
            <span>Positive Days</span>
            <strong>{num(wins)}</strong>
          </div>

          <div className="metricTile">
            <span>Negative Days</span>
            <strong>{num(losses)}</strong>
          </div>

          <div className="metricTile">
            <span>Max Drawdown</span>
            <strong style={{ color: '#ff5c7a' }}>{money(maxDrawdown)}</strong>
          </div>

          <div className="metricTile">
            <span>Drawdown %</span>
            <strong style={{ color: '#ff5c7a' }}>{pct(maxDrawdownPct * 100)}</strong>
          </div>
        </div>

        {/* ---------- CHART ---------- */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            height: 390,
            borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.08)',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
            padding: '12px 8px 4px 0'
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 18, right: 30, left: 8, bottom: 8 }}
            >
              <defs>
                <linearGradient id="meicEquityFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B18800" stopOpacity={0.28} />
                  <stop offset="55%" stopColor="#B18800" stopOpacity={0.08} />
                  <stop offset="100%" stopColor="#B18800" stopOpacity={0} />
                </linearGradient>

                <linearGradient id="meicLiveLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#B18800" />
                  <stop offset="100%" stopColor="#F1D27A" />
                </linearGradient>

                <linearGradient id="meicBacktestLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.44)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0.90)" />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="rgba(255,255,255,0.07)"
                strokeDasharray="4 8"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                minTickGap={30}
                tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.42)'
                }}
                tickFormatter={shortDate}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.42)'
                }}
                tickFormatter={(v) => `$${num(v)}`}
                width={88}
                domain={[yMinPadded, yMaxPadded]}
                allowDataOverflow
                scale="linear"
                type="number"
              />

              <ReferenceLine
                y={START_EQUITY}
                stroke="rgba(255,255,255,0.24)"
                strokeDasharray="5 5"
                strokeWidth={1}
                label={{
                  value: 'Starting Equity',
                  position: 'insideTopLeft',
                  fill: 'rgba(255,255,255,0.42)',
                  fontSize: 11,
                  fontWeight: 700
                }}
              />

              <ReferenceLine
                x={LIVE_START}
                stroke="rgba(241,210,122,0.65)"
                strokeDasharray="6 6"
                strokeWidth={1.3}
                label={{
                  value: 'Live Start',
                  position: 'insideTopRight',
                  fill: 'rgba(241,210,122,0.9)',
                  fontSize: 11,
                  fontWeight: 800
                }}
              />

              <Tooltip
                cursor={{
                  stroke: 'rgba(241,210,122,0.35)',
                  strokeWidth: 1,
                  strokeDasharray: '4 4'
                }}
                content={<CustomTooltip />}
              />

              <Area
                type="monotone"
                dataKey="equity_fill"
                fill="url(#meicEquityFill)"
                stroke="transparent"
                connectNulls={false}
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="equity_backtest"
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: 'rgba(255,255,255,0.9)',
                  strokeWidth: 2,
                  fill: '#0f1219'
                }}
                strokeWidth={2}
                stroke="url(#meicBacktestLine)"
                connectNulls={false}
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="equity_live"
                dot={false}
                activeDot={{
                  r: 5,
                  stroke: '#F1D27A',
                  strokeWidth: 2,
                  fill: '#0f1219'
                }}
                strokeWidth={2.75}
                stroke="url(#meicLiveLine)"
                connectNulls={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* ---------- LEGEND ---------- */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 12,
            alignItems: 'center',
            marginTop: 13,
            color: 'rgba(255,255,255,0.48)',
            fontSize: 12
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
              <span
                style={{
                  width: 18,
                  height: 3,
                  borderRadius: 99,
                  background: 'rgba(255,255,255,0.76)',
                  display: 'inline-block'
                }}
              />
              Backtest
            </div>

            <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
              <span
                style={{
                  width: 18,
                  height: 3,
                  borderRadius: 99,
                  background: '#B18800',
                  display: 'inline-block'
                }}
              />
              Live
            </div>
          </div>
        </div>

        <style>{`
          .miniStatModern {
            min-width: 108px;
            padding: 10px 12px;
            border-radius: 15px;
            border: 1px solid rgba(255,255,255,0.09);
            background: rgba(255,255,255,0.045);
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
          }

          .miniLabelModern {
            font-size: 10px;
            letter-spacing: 0.11em;
            text-transform: uppercase;
            color: rgba(255,255,255,0.42);
            font-weight: 800;
            margin-bottom: 5px;
          }

          .miniValueModern {
            font-size: 15px;
            font-weight: 850;
            color: rgba(255,255,255,0.92);
          }

          .metricTile {
            padding: 11px 12px;
            border-radius: 15px;
            border: 1px solid rgba(255,255,255,0.075);
            background: rgba(0,0,0,0.18);
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: center;
          }

          .metricTile span {
            font-size: 11px;
            color: rgba(255,255,255,0.44);
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }

          .metricTile strong {
            font-size: 14px;
            color: rgba(255,255,255,0.9);
            font-weight: 850;
          }

          @media (max-width: 820px) {
            .metricTile {
              align-items: flex-start;
              flex-direction: column;
              gap: 5px;
            }
          }

          @media (max-width: 680px) {
            .miniStatModern {
              min-width: calc(50% - 5px);
            }
          }
        `}</style>
      </div>

      {/* ---------- DAILY PNL TABLE ---------- */}
      <div
        style={{
          marginTop: 18,
          borderRadius: 22,
          border: '1px solid rgba(255,255,255,0.10)',
          background:
            'linear-gradient(180deg, rgba(15,18,27,0.92), rgba(5,7,11,0.96))',
          boxShadow:
            '0 18px 55px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            alignItems: 'center',
            padding: '16px 18px',
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.13em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.48)',
                fontWeight: 800,
                marginBottom: 5
              }}
            >
              MEIC Daily PnL
            </div>

            <div
              style={{
                color: 'rgba(255,255,255,0.88)',
                fontSize: 15,
                fontWeight: 800
              }}
            >
              Recent Session Results
            </div>
          </div>

          <div
            style={{
              color: 'rgba(255,255,255,0.44)',
              fontSize: 12,
              fontWeight: 700
            }}
          >
            Last {num(tableRows.length)} sessions
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: 620
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Equity</th>
                <th style={thStyle}>Daily PnL</th>
                <th style={thStyle}>Daily Return</th>
              </tr>
            </thead>

            <tbody>
              {tableRows.map((r) => {
                const pnl = Number(r.daily_pnl);
                const isPositive = pnl > 0;
                const isNegative = pnl < 0;

                return (
                  <tr
                    key={r.id ?? r.date}
                    style={{
                      borderTop: '1px solid rgba(255,255,255,0.06)'
                    }}
                  >
                    <td style={tdStyle}>{shortDate(r.date)}</td>
                    <td style={tdStyle}>{money(r.equity)}</td>
                    <td
                      style={{
                        ...tdStyle,
                        color: isPositive
                          ? '#37d67a'
                          : isNegative
                            ? '#ff5c7a'
                            : 'rgba(255,255,255,0.78)',
                        fontWeight: 800
                      }}
                    >
                      {money(r.daily_pnl)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: Number(r.daily_return) > 0
                          ? '#37d67a'
                          : Number(r.daily_return) < 0
                            ? '#ff5c7a'
                            : 'rgba(255,255,255,0.78)',
                        fontWeight: 800
                      }}
                    >
                      {pctAlready(r.daily_return)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  padding: '12px 18px',
  fontSize: 10,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.42)',
  fontWeight: 850,
  background: 'rgba(255,255,255,0.025)'
};

const tdStyle = {
  padding: '13px 18px',
  fontSize: 13,
  color: 'rgba(255,255,255,0.78)',
  fontWeight: 650,
  whiteSpace: 'nowrap'
};















